import { InputValueMap } from '@/utils/new-order';
import { OrderSummaryInputID } from '@/enums';
import { useCallback } from 'react';
import { useSubmitSalesPortalOrderMutation } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/blocs/documents/place-sales-portal-order.generated';

/**
 * Mutator hook to handle order submission.
 *
 * `submitOrder` accepts the additional input values map plus the order UUID.
 * It sanitizes PO number and order notes into dedicated fields and sends all
 * remaining custom checkout fields in `additional_input_values`.
 *
 * @returns {function} submitOrder - The function to submit an order. Accepts
 *   (additionalInputValueMap, orderUUID) and resolves to the mutation result.
 * @returns {boolean} loading - If true, the mutation is loading.
 * @returns {Object} error - If error, the ApolloError object.
 * @returns {Object} data - The place order mutation data.
 *
 * @example
 * ```typescript
 * const { submitOrder, loading: isSubmittingOrder } = useSubmitOrderMutator();
 * const handleSubmitOrder = useCallback(async () => {
 *   if (!orderUUID) {
 *     return;
 *   }
 *   const result = await submitOrder(additionalInputValueMap, orderUUID);
 *   // assume success if we get an order id
 *   if (result.data?.submitOrder.order_id) {
 *     // handle success logic
 *   } else {
 *     // handle error logic
 *   }
 * }, [additionalInputValueMap, orderUUID, submitOrder]);
 * ```
 */
export const useSubmitOrderMutator = () => {
  const [submitOrder, { data, loading, error }] =
    useSubmitSalesPortalOrderMutation();

  const handleSubmitOrder = useCallback(
    async (orderUUID: string, additionalInputValueMap: InputValueMap) => {
      // Extract the po number and order notes specifically, as they have dedicated fields,
      // custom checkout fields are sent in `additional_input_values`.
      const rawOrderNotes =
        additionalInputValueMap[OrderSummaryInputID.Notes]?.value ?? '';
      const rawPoNumber =
        additionalInputValueMap[OrderSummaryInputID.PONumber]?.value ?? '';

      const orderNotes = rawOrderNotes.trim().length > 0 ? rawOrderNotes : null;
      const poNumber = rawPoNumber.trim().length > 0 ? rawPoNumber : null;

      // Filter out PO Number and Order Notes from additional inputs
      const additionalInputValues = Object.entries(additionalInputValueMap)
        .filter(
          ([id, { value }]) =>
            id !== OrderSummaryInputID.Notes &&
            id !== OrderSummaryInputID.PONumber &&
            value.trim().length > 0,
        )
        .map(([id, { title, value }]) => ({
          id,
          title,
          value,
        }));

      return submitOrder({
        variables: {
          additionalInputValues,
          orderNotes,
          orderUUID,
          poNumber,
        },
      });
    },
    [submitOrder],
  );

  return {
    data,
    error,
    loading,
    submitOrder: handleSubmitOrder,
  };
};
